import React from 'react';
import { motion } from 'framer-motion';
import Card from '../ui/Card';
import './AdminStatsCards.css';

/**
 * Admin Stats Cards - Dashboard statistics overview
 */
const AdminStatsCards = ({ stats }) => {
  if (!stats) return null;

  const statCards = [
    {
      title: 'Total Users',
      value: stats.users?.total || 0,
      subStats: [
        { label: 'Verified', value: stats.users?.verified || 0, color: 'success' },
        { label: 'Unverified', value: stats.users?.unverified || 0, color: 'warning' },
        { label: 'New This Month', value: stats.users?.new_this_month || 0, color: 'info' },
      ],
      icon: '👥',
      color: 'primary',
      trend: '+12%'
    },
    {
      title: 'Designers',
      value: stats.designers?.total || 0,
      subStats: [
        { label: 'Approved', value: stats.designers?.approved || 0, color: 'success' },
        { label: 'Pending', value: stats.designers?.pending || 0, color: 'warning' },
        { label: 'Rejected', value: stats.designers?.rejected || 0, color: 'error' },
      ],
      icon: '👗',
      color: 'accent',
      trend: '+8%'
    },
    {
      title: 'Products',
      value: stats.products?.total || 0,
      subStats: [
        { label: 'In Stock', value: stats.products?.in_stock || 0, color: 'success' },
        { label: 'Out of Stock', value: stats.products?.out_of_stock || 0, color: 'error' },
        { label: 'Avg Price', value: `₹${Math.round(stats.products?.avg_price || 0)}`, color: 'info' },
      ],
      icon: '🏷️',
      color: 'success',
      trend: '+15%'
    },
    {
      title: 'Orders',
      value: stats.orders?.total || 0,
      subStats: [
        { label: 'Completed', value: stats.orders?.completed || 0, color: 'success' },
        { label: 'In Progress', value: stats.orders?.in_progress || 0, color: 'warning' },
        { label: 'Pending', value: stats.orders?.pending || 0, color: 'info' },
      ],
      icon: '📦',
      color: 'warning',
      trend: '+22%'
    },
    {
      title: 'Revenue',
      value: `₹${(stats.orders?.total_revenue || 0).toLocaleString()}`,
      subStats: [
        { label: 'This Month', value: '₹45,890', color: 'success' },
        { label: 'Avg Order', value: `₹${Math.round((stats.orders?.total_revenue || 0) / (stats.orders?.total || 1))}`, color: 'info' },
        { label: 'Commission', value: '₹12,340', color: 'accent' },
      ],
      icon: '💰',
      color: 'success',
      trend: '+18%'
    },
    {
      title: 'Bidding',
      value: stats.bidding?.total_bids || 0,
      subStats: [
        { label: 'Accepted', value: stats.bidding?.accepted_bids || 0, color: 'success' },
        { label: 'Declined', value: stats.bidding?.declined_bids || 0, color: 'error' },
        { label: 'Pending', value: stats.bidding?.pending_bids || 0, color: 'warning' },
      ],
      icon: '💰',
      color: 'info',
      trend: '+5%'
    },
    {
      title: 'Payouts',
      value: `₹${(stats.payouts?.total_payout_amount || 0).toLocaleString()}`,
      subStats: [
        { label: 'Successful', value: stats.payouts?.successful_payouts || 0, color: 'success' },
        { label: 'Pending', value: stats.payouts?.pending_payouts || 0, color: 'warning' },
        { label: 'Total Count', value: stats.payouts?.total_payouts || 0, color: 'info' },
      ],
      icon: '💳',
      color: 'accent',
      trend: '+10%'
    },
    {
      title: 'System Health',
      value: '98.5%',
      subStats: [
        { label: 'Uptime', value: '99.9%', color: 'success' },
        { label: 'API Calls', value: '2.3M', color: 'info' },
        { label: 'Errors', value: '0.1%', color: 'success' },
      ],
      icon: '⚡',
      color: 'success',
      trend: '+0.2%'
    },
  ];

  return (
    <div className="admin-stats">
      <div className="admin-stats__grid">
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <Card
              className={`admin-stat-card admin-stat-card--${card.color}`}
              hoverable
            >
              <div className="admin-stat-card__header">
                <div className="admin-stat-card__info">
                  <div className="admin-stat-card__icon">{card.icon}</div>
                  <div className="admin-stat-card__title">{card.title}</div>
                </div>
                <div className={`admin-stat-card__trend admin-stat-card__trend--positive`}>
                  {card.trend}
                </div>
              </div>

              <div className="admin-stat-card__value">{card.value}</div>

              <div className="admin-stat-card__substats">
                {card.subStats.map((subStat, subIndex) => (
                  <div
                    key={subIndex}
                    className={`admin-stat-card__substat admin-stat-card__substat--${subStat.color}`}
                  >
                    <span className="admin-stat-card__substat-label">{subStat.label}</span>
                    <span className="admin-stat-card__substat-value">{subStat.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AdminStatsCards;